import { useState } from "react";
import * as yup from "yup";

import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import EntityTable, { StatusBadge, RowActionMenu } from "components/EntityTable";
import ConfirmDialog from "components/ConfirmDialog";
import EntityFormDialog from "components/EntityFormDialog";
import useUsersTable from "hooks/useUsersTable";
import { formatDate } from "utils/datetime";

const createUserSchema = yup.object({
  email: yup.string().email("Enter a valid email").required("Email is required"),
  password: yup.string().min(6, "Min 6 characters").required("Password is required"),
  role: yup.string().required("Role is required"),
});

const editUserSchema = yup.object({
  email: yup.string().email("Enter a valid email").required("Email is required"),
  role: yup.string().required("Role is required"),
});

// Standalone Users page — a paginated table of every system user.
function Users() {
  const users = useUsersTable();

  const [form, setForm] = useState({ open: false, mode: "create", user: null });
  const [confirm, setConfirm] = useState({ open: false, user: null });
  const [submitting, setSubmitting] = useState(false);

  const openCreate = () => setForm({ open: true, mode: "create", user: null });
  const openEdit = (user) => setForm({ open: true, mode: "edit", user });
  const closeForm = () => setForm((f) => ({ ...f, open: false }));

  const toggleActive = (user) =>
    user.active ? users.deactivate(user.id) : users.activate(user.id);

  const handleFormSubmit = async (values) => {
    setSubmitting(true);
    try {
      if (form.mode === "create") {
        await users.create(values);
      } else {
        await users.update(form.user.id, { email: values.email, role: values.role });
      }
      closeForm();
    } catch {
      // useUsersTable already surfaced the error via snackbar.
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmDelete = async () => {
    setSubmitting(true);
    try {
      await users.remove(confirm.user.id);
      setConfirm({ open: false, user: null });
    } catch {
      // useUsersTable already surfaced the error via snackbar.
    } finally {
      setSubmitting(false);
    }
  };

  const columns = [
    { field: "email", headerName: "Email", flex: 1.4, minWidth: 220 },
    { field: "role", headerName: "Role", flex: 0.8, minWidth: 120 },
    {
      field: "active",
      headerName: "Status",
      flex: 0.7,
      minWidth: 120,
      renderCell: ({ value }) => <StatusBadge active={value} />,
    },
    {
      field: "created_at",
      headerName: "Created",
      flex: 0.9,
      minWidth: 130,
      renderCell: ({ value }) => formatDate(value),
    },
    {
      field: "updated_at",
      headerName: "Updated",
      flex: 0.9,
      minWidth: 130,
      renderCell: ({ value }) => formatDate(value),
    },
    {
      field: "actions",
      headerName: "",
      width: 64,
      align: "center",
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <RowActionMenu
          actions={[
            { label: "Edit", icon: "edit", onClick: () => openEdit(row) },
            {
              label: row.active ? "Deactivate" : "Activate",
              icon: row.active ? "block" : "check_circle",
              onClick: () => toggleActive(row),
            },
            {
              label: "Delete",
              icon: "delete",
              color: "error",
              onClick: () => setConfirm({ open: true, user: row }),
            },
          ]}
        />
      ),
    },
  ];

  const isEdit = form.mode === "edit";
  const formFields = isEdit
    ? [
        { name: "email", label: "Email", type: "email", required: true },
        { name: "role", label: "Role", required: true },
      ]
    : [
        { name: "email", label: "Email", type: "email", required: true },
        { name: "password", label: "Password", type: "password", required: true },
        { name: "role", label: "Role", required: true },
      ];
  const formInitialValues = isEdit
    ? { email: form.user?.email || "", role: form.user?.role || "" }
    : { email: "", password: "", role: "" };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={2}
          mb={3}
        >
          <MDTypography variant="h4" fontWeight="bold">
            Users
          </MDTypography>
          <MDButton
            variant="gradient"
            color="info"
            startIcon={<Icon>add</Icon>}
            onClick={openCreate}
          >
            Add User
          </MDButton>
        </MDBox>

        <Card>
          <MDBox p={3}>
            <EntityTable
              rows={users.rows}
              columns={columns}
              getRowId={(row) => row.id}
              loading={users.loading}
              rowCount={users.total}
              page={users.page}
              pageSize={users.pageSize}
              onPageChange={users.setPage}
              onPageSizeChange={users.setPageSize}
            />
          </MDBox>
        </Card>
      </MDBox>

      <EntityFormDialog
        open={form.open}
        title={isEdit ? "Edit User" : "Add New User"}
        fields={formFields}
        initialValues={formInitialValues}
        validationSchema={isEdit ? editUserSchema : createUserSchema}
        submitText={isEdit ? "Save" : "Create"}
        loading={submitting}
        onSubmit={handleFormSubmit}
        onClose={closeForm}
      />

      <ConfirmDialog
        open={confirm.open}
        title="Delete User"
        message={`Delete user "${confirm.user?.email || ""}"? This cannot be undone.`}
        confirmText="Delete"
        loading={submitting}
        onConfirm={handleConfirmDelete}
        onClose={() => setConfirm({ open: false, user: null })}
      />
    </DashboardLayout>
  );
}

export default Users;
